import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { X, Flag } from "lucide-react";

const REASONS = [
  "Inappropriate content",
  "Harassment or bullying",
  "Fake profile or impersonation",
  "Spam or scam",
  "Safety concern involving a minor",
  "Other",
];

export const ReportDialog = ({ targetType, targetId, targetLabel, onClose }) => {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!reason) return;
    setSending(true);
    try {
      const { data } = await api.post("/reports", {
        target_type: targetType,
        target_id: targetId,
        reason: details.trim() ? `${reason}: ${details.trim()}` : reason,
      });
      toast.success(data.duplicate ? "You already reported this — our team is on it." : "Report sent. Thank you for keeping EGNCY safe.");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="report-dialog"
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#121212] border border-[#262626] w-full max-w-md rounded-sm p-6 space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl uppercase tracking-tight text-white flex items-center gap-2">
            <Flag className="w-4 h-4" /> Report {targetType}
          </h2>
          <button type="button" data-testid="report-close" onClick={onClose} className="text-[#a3a3a3] hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        {targetLabel && <p className="text-sm text-[#a3a3a3]">Reporting: <span className="text-white">{targetLabel}</span></p>}
        <div className="space-y-2">
          {REASONS.map((r) => (
            <button
              key={r}
              type="button"
              data-testid={`report-reason-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setReason(r)}
              className={`w-full text-left px-3 py-2 border rounded-sm text-sm transition-colors ${
                reason === r ? "border-white bg-[#1a1a1a] text-white" : "border-[#262626] text-[#a3a3a3] hover:border-white hover:text-white"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
        <textarea
          data-testid="report-details"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          rows={2}
          maxLength={400}
          placeholder="Add details (optional)"
          className="w-full bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none p-3 rounded-sm text-white text-sm resize-none transition-colors"
        />
        <button
          data-testid="report-submit"
          disabled={!reason || sending}
          className="w-full bg-white text-black font-semibold py-2.5 rounded-sm hover:bg-gray-200 disabled:opacity-40 transition-colors text-sm"
        >
          {sending ? "Sending…" : "Send report"}
        </button>
      </form>
    </div>
  );
};
